import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { mutate } from "swr";

import { useUser } from "context/UserContext";
import { useFirebase } from "firebase";
import { PortalContent } from "layouts/PortalLayout";
import Loading from "components/Loading";
import { usePOST, NetworkError } from "hooks/usePublicAPI";
import { usePhoneUpdateVerification } from "hooks/usePhoneUpdateVerification";
import { formatPhoneNumber } from "helpers/phone";
import OtpVerification from "components/OtpVerification";
import type { SubmitOutcome } from "components/OtpVerification/ended-code";
import Modal from "components/Modal";
import PageHeader from "components/PageHeader";
import ProfileSection from "components/Account/ProfileSection";
import SecuritySection from "components/Account/SecuritySection";
import ChangeContactModal from "components/Account/ChangeContactModal";

type RequestVerificationPayload = {
  method: "email" | "sms";
  target: string;
  intent: "verify_email" | "verify_phone" | "verify_email_and_set_password";
};
type RequestVerificationResponse = {
  success: boolean;
  message?: string;
  error?: string;
};
type VerifyPayload = {
  token?: string;
  firebase_id_token?: string;
  verified_phone_number?: string;
};
type VerifyResponse = {
  success: boolean;
  custom_token?: string;
  next_step?: string;
  error?: string;
};

export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useUser();
  const { auth, signInWithCustomToken, logout } = useFirebase();
  const [pageMessage, setPageMessage] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const [isVerifyingSms, setIsVerifyingSms] = useState(false);
  const [verificationTarget, setVerificationTarget] = useState<string | null>(
    null,
  );

  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [changeModalType, setChangeModalType] = useState<
    "email" | "phone" | null
  >(null);
  const [changeModalInputError, setChangeModalInputError] = useState<
    string | null
  >(null);

  const {
    trigger: triggerRequestVerification,
    isMutating: isRequestingVerification,
    error: requestVerificationError,
  } = usePOST<
    RequestVerificationResponse,
    NetworkError,
    RequestVerificationPayload
  >("/me/request-verification");

  const {
    trigger: triggerVerify,
    isMutating: isVerifying,
    error: verifyError,
  } = usePOST<VerifyResponse, NetworkError, VerifyPayload>("/auth/verify");

  const {
    isVerifying: phoneHookIsVerifying,
    error: phoneHookError,
    clearError: clearPhoneHookError,
    startVerification: startFirebasePhoneUpdateVerification,
    verifyCode: verifyFirebasePhoneUpdateCode,
  } = usePhoneUpdateVerification();

  useEffect(() => {
    if (location.state?.message) {
      setPageMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    // Prioritize showing specific verifyError or requestVerificationError
    // over potential phoneHookError during the same flow.
    const apiError = verifyError || requestVerificationError;
    if (apiError) {
      console.error("Account Page API Error:", apiError);
      let friendlyMessage = "An unexpected error occurred.";
      if (typeof apiError === "object" && apiError !== null) {
        if (
          "statusCode" in apiError &&
          typeof (apiError as any).statusCode === "number"
        ) {
          const netError = apiError as NetworkError;
          friendlyMessage =
            netError.responseData?.error || netError.message || friendlyMessage;
          // Specific error message check for pending verification not found
          if (
            netError.responseData?.error?.includes(
              "No matching pending SMS verification found",
            )
          ) {
            friendlyMessage =
              "Verification failed. Please try requesting a new code.";
          }
        } else if (
          "message" in apiError &&
          typeof (apiError as any).message === "string"
        ) {
          friendlyMessage = (apiError as Error).message;
        }
      }
      setPageError(friendlyMessage);
      // Clear phone hook error if API error takes precedence
      clearPhoneHookError();
    } else if (phoneHookError) {
      // Show phone hook error only if no API error occurred
      console.error("Account Page Phone Update Hook Error:", phoneHookError);
      setPageError(phoneHookError);
    } else {
      setPageError(null); // Clear error if hooks reset
    }
  }, [
    requestVerificationError,
    verifyError,
    phoneHookError,
    clearPhoneHookError,
  ]);

  /** Starts verifying a new email or phone; resolves true when the email or
   * code was sent. */
  const handleInitiateVerification = async (
    payload: RequestVerificationPayload,
  ): Promise<boolean> => {
    setPageError(null);
    setPageMessage(null);
    setIsVerifyingSms(false);
    setVerificationTarget(null);
    setIsChangeModalOpen(false);
    setChangeModalInputError(null);
    setIsVerifyingSms(false); // Ensure OTP modal is closed initially
    setVerificationTarget(null);
    setIsChangeModalOpen(false); // Close modal if open
    setChangeModalInputError(null);
    clearPhoneHookError(); // Clear any previous phone errors

    try {
      const response = await triggerRequestVerification(payload);
      if (response?.success) {
        if (payload.method === "email") {
          setPageMessage(
            `Verification email sent to ${payload.target}. Please check your inbox.`,
          );
          return true;
        } else if (payload.method === "sms") {
          setVerificationTarget(payload.target);
          // SMS update flow: Use the new hook to start Firebase verification
          const otpSent = await startFirebasePhoneUpdateVerification(
            payload.target,
          );
          if (otpSent) {
            setIsVerifyingSms(true); // Show OTP modal
            return true;
          } // Error handled by effect listening to phoneHookError
        }
      } else {
        // Error set by effect listening to requestVerificationError
        // We can still optionally set a fallback message here if needed
        // setPageError(response?.error || "Failed to start verification.");
      }
    } catch (err) {
      console.error("Error triggering request verification:", err);
      // Error should ideally be caught by usePOST and handled by effect
      setPageError(
        "An unexpected client error occurred while requesting verification.",
      );
    }
    return false;
  };

  const openChangeModal = (type: "email" | "phone") => {
    setChangeModalType(type);
    setChangeModalInputError(null); // Clear previous modal error
    setIsChangeModalOpen(true);
  };

  const handleChangeContactSubmit = (
    newValue: string,
    type: "email" | "phone",
  ) => {
    setChangeModalInputError(null); // Clear previous modal error

    if (type === "email" && !newValue.includes("@")) {
      setChangeModalInputError("Please enter a valid email address.");
      return;
    }
    if (type === "phone") {
      const digits = newValue.replace(/\D/g, "");
      if (digits.length < 10) {
        setChangeModalInputError("Please enter a valid phone number.");
        return;
      }
    }

    // Format phone number
    let targetToSend = newValue;
    let methodToSend: "email" | "sms" = type === "email" ? "email" : "sms";
    if (type === "phone") {
      const phoneNumber = newValue.replace(/\D/g, "");
      targetToSend =
        phoneNumber.startsWith("1") && phoneNumber.length === 11
          ? `+${phoneNumber}`
          : phoneNumber.length === 10
            ? `+1${phoneNumber}`
            : `+${phoneNumber}`;
    }

    handleInitiateVerification({
      method: methodToSend,
      target: targetToSend,
      intent: type === "email" ? "verify_email" : "verify_phone",
    });
  };

  const handleVerifySMSCode = async (code: string): Promise<SubmitOutcome> => {
    clearPhoneHookError(); // Clear previous hook errors
    setPageError(null); // Clear general page errors

    if (!verificationTarget) {
      console.error("Missing target for OTP submission.");
      setPageError("Verification session error. Please try again.");
      setIsVerifyingSms(false);
      setVerificationTarget(null);
      return undefined;
    }

    // Step 1: Verify code and update Firebase user record using the new hook
    const firebaseUpdate = await verifyFirebasePhoneUpdateCode(code);
    if (firebaseUpdate === "code_ended") return "code_ended";

    if (firebaseUpdate === "updated") {
      // Step 2: Notify our backend to finalize its verification record
      try {
        const response = await triggerVerify({
          verified_phone_number: verificationTarget,
        });
        handleVerifyResponse(response, true); // Pass flag indicating phone update
      } catch (err) {
        console.error("Error triggering /auth/verify for phone update:", err);
        // Error handled by effect listening to verifyError
        // If the backend call fails, we might want a specific message
        setPageError(
          "Phone number updated, but failed to confirm with server. Please refresh or contact support if issues persist.",
        );
      }
      // Close modal and clear target only after *successful* backend call or handled error
      setIsVerifyingSms(false);
      setVerificationTarget(null);
    } else {
      // Firebase update failed - error is set by the effect listening to phoneHookError
      // Keep the modal open for the user to retry the code.
    }
    return undefined;
  };

  // Common handler for /auth/verify response
  const handleVerifyResponse = async (
    response: VerifyResponse | undefined,
    isPhoneUpdate: boolean = false, // Flag to differentiate flows
  ) => {
    if (response?.success) {
      if (isPhoneUpdate) {
        // Phone update success (no custom token expected)
        mutate("/me"); // Revalidate user data
        setPageMessage("Phone number updated successfully!");
        setPageError(null);
      } else if (response.custom_token) {
        // Initial verification/linking success
        try {
          await signInWithCustomToken(auth, response.custom_token);
          mutate("/me"); // Revalidate user data
          if (response.next_step === "set_password") {
            navigate("/set-password");
          } else {
            setPageMessage("Verification successful!");
            setPageError(null);
          }
        } catch (signInError) {
          console.error("Error signing in with custom token:", signInError);
          setPageError(
            "Verification succeeded, but failed to refresh session. Please log in again.",
          );
        }
      }
    } else {
      // API call failed or returned { success: false }
      // Error message should be set by the useEffect hook listening to verifyError.
      // Fallback (ensure we don't overwrite a more specific hook error):
      if (!pageError && !phoneHookError) {
        // Check phoneHookError too
        setPageError(response?.error || "Verification failed.");
      }
    }
  };

  if (loading) {
    return (
      <>
        <PortalContent>
          <Loading message="Loading Account..." />
        </PortalContent>
      </>
    );
  }

  const isEmailVerified = user!.email_verified;
  const isPhoneVerified = user!.auth_providers.includes("phone");
  const hasPasswordAuth = user!.auth_providers.includes("password");

  return (
    <>
      <PortalContent className="space-y-8">
        {pageMessage && (
          <div className="rounded-xl bg-green-100 p-3 text-sm text-green-700">
            {pageMessage}
          </div>
        )}
        {/* Show general page error unless OTP modal is open (which shows phoneHookError) */}
        {pageError && !isVerifyingSms && (
          <div className="rounded-xl bg-red-100 p-3 text-sm text-red-700">
            {pageError}
          </div>
        )}

        <PageHeader
          eyebrow="Account"
          title="Account Settings"
          description="Manage your After account"
        />

        <ProfileSection
          user={user!}
          isEmailVerified={isEmailVerified}
          isPhoneVerified={isPhoneVerified}
          isRequestingVerification={isRequestingVerification}
          onVerifyRequest={handleInitiateVerification}
          onChangeRequest={openChangeModal}
        />

        <SecuritySection
          user={user!}
          hasPasswordAuth={hasPasswordAuth}
          isRequestingVerification={isRequestingVerification}
          onSetupRequest={handleInitiateVerification}
        />

        <div id="recaptcha-container"></div>

        <button
          type="button"
          onClick={async () => {
            await logout();
          }}
          className="text-sm font-bold text-evergreen underline hover:text-soil"
        >
          Sign out
        </button>
      </PortalContent>

      {/* SMS OTP Modal */}
      <Modal
        isOpen={isVerifyingSms}
        onClose={() => {
          setIsVerifyingSms(false);
          setVerificationTarget(null);
          clearPhoneHookError(); // Clear error if modal is closed manually
        }}
        title="Enter Verification Code"
      >
        <OtpVerification
          type="sms"
          destinationDisplay={formatPhoneNumber(verificationTarget || "")}
          onSubmit={handleVerifySMSCode}
          onResend={async (trigger) => {
            if (!verificationTarget) return false;
            if (trigger === "code_ended") {
              return startFirebasePhoneUpdateVerification(
                verificationTarget,
                trigger,
              );
            }
            return handleInitiateVerification({
              method: "sms",
              target: verificationTarget,
              intent: "verify_phone",
            });
          }}
          error={phoneHookError || pageError}
          isLoading={phoneHookIsVerifying || isVerifying}
        />
      </Modal>

      <ChangeContactModal
        isOpen={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        type={changeModalType}
        onSubmit={handleChangeContactSubmit}
        isLoading={isRequestingVerification || phoneHookIsVerifying}
        error={changeModalInputError}
      />
    </>
  );
}
